import React from 'react'
import Link from 'next/link'
import queryString from 'query-string'

import { Button } from '@wartek-id/button'
import { Icon } from '@wartek-id/icon'
import { Text } from '@wartek-id/text'

import { EmptyState, EmptyStateAction } from 'components/EmptyState'
import { Anchor } from 'components/Anchor'
import PelatihanDetailShell from '../components/PelatihanDetailShell'
import CertificateProfileRedirectCard from '../components/CertificateAksiNyataCard'
import CertificateTopic from '../components/CertificateTopic'
import AksiNyataCard from '../partials/AksiNyata/AksiNyataCard'
import ModuleCard from '../partials/Module/ModuleCard'
import ModulePlaceholder from '../partials/Module/ModulePlaceholder'
import WebinarCard from '../partials/Webinar/WebinarCard'
import ModuleDetails from './ModuleDetails'

import type {
  CertificateStatusType,
  WebinarEntry,
} from 'api/microlearning/types'
import type { Module, OverallProgress, TopicDetails } from 'types/microlearning'

type TopicDetailsDesktopProps = {
  certificateStatus?: CertificateStatusType
  fetchStatus: 'idle' | 'loading' | 'success' | 'error'
  isAksiNyataEnabled: boolean
  isAksiNyataLocked: boolean
  menuId: string | string[]
  moduleWithProgress: Module[]
  onModuleClick: (id: string | number) => void
  onShareClick: () => void
  progressionData?: OverallProgress
  selectedModuleId?: string
  source: string | string[]
  topicData?: TopicDetails
  topicId: string
  webinar?: WebinarEntry
}

const TopicDetailsDesktop = ({
  certificateStatus,
  fetchStatus,
  isAksiNyataEnabled,
  isAksiNyataLocked,
  menuId,
  moduleWithProgress,
  onModuleClick,
  onShareClick,
  progressionData,
  selectedModuleId,
  source,
  topicData,
  topicId,
  webinar,
}: TopicDetailsDesktopProps) => {
  const topicName = topicData?.name ?? 'Topik'
  const moduleSource = Array.isArray(source) ? source[0] : source
  const moduleMenuId = Array.isArray(menuId) ? menuId[0] : menuId
  const topicQuery = {
    from: moduleSource,
    menuId: moduleMenuId,
  }

  if (fetchStatus === 'error') {
    return (
      <PelatihanDetailShell
        breadcrumbs={[
          { label: 'Pelatihan Mandiri', href: '/pelatihan-mandiri' },
          { label: topicName, href: null },
        ]}
      >
        <div className="rounded-lg border border-gray-20 bg-white p-8">
          <EmptyState
            fullscreen={false}
            className="min-h-[320px]"
            title="Data gagal dimuat"
            description="Ada kesalahan pada sistem. Silakan coba beberapa saat lagi."
          >
            <EmptyStateAction
              className="mb-4 max-w-sm"
              onClick={() => window.location.reload()}
            >
              Muat Ulang
            </EmptyStateAction>
          </EmptyState>
        </div>
      </PelatihanDetailShell>
    )
  }

  const sidebar = (
    <div className="rounded-lg border border-gray-20 bg-white p-5">
      <Text variant="subheading" color="subdued" className="mb-2 uppercase">
        Topik
      </Text>
      <Text variant="heading-lg" className="mb-5">
        {topicName}
      </Text>

      {topicData?.values?.length ? (
        <div className="mb-4">
          <Text variant="heading-sm" className="mb-2">
            Yang akan dipelajari
          </Text>
          <ul className="list-disc space-y-1 pl-5">
            {topicData.values.map((value) => (
              <Text as="li" variant="body-sm" key={value}>
                {value}
              </Text>
            ))}
          </ul>
        </div>
      ) : null}

      {topicData?.contextualizedPageId ? (
        <Anchor
          href={`/pelatihan-mandiri/topik/${topicId}/referensi?${queryString.stringify(
            topicQuery
          )}`}
        >
          <Button as="span" color="white" size="sm" className="mb-6">
            Buka Pengayaan
          </Button>
        </Anchor>
      ) : null}

      <Text variant="heading-sm" className="mb-1">
        Modul Pelatihan
      </Text>
      <Text variant="body-sm" color="subdued" className="mb-3">
        Pelajari modul sesuai urutan.
      </Text>
      {fetchStatus === 'loading' ? (
        <ModulePlaceholder amount={3} />
      ) : (
        <div className="space-y-3">
          {moduleWithProgress.map(
            (
              { id, name, duration, totalMaterial, progress, badgeText },
              index
            ) => {
              const isLocked = !progress?.prerequisiteCompleted
              const isActive = String(id) === selectedModuleId

              return (
                <div
                  key={id}
                  onClick={() => {
                    if (!isLocked) onModuleClick(id)
                  }}
                >
                  <ModuleCard
                    id={id}
                    number={index + 1}
                    name={name}
                    duration={duration}
                    totalMaterial={totalMaterial}
                    isActive={isActive}
                    isLocked={isLocked}
                    isCompleted={progress?.postTest === 'passed'}
                    badgeText={badgeText}
                    href={
                      `/pelatihan-mandiri/topik/${topicId}?` +
                      queryString.stringify({
                        ...topicQuery,
                        modul: id,
                      })
                    }
                  />
                </div>
              )
            }
          )}
        </div>
      )}

      <div className="mt-6 space-y-4 border-t border-gray-20 pt-6">
        {webinar ? (
          <div>
            <div className="mb-2 flex items-center justify-between gap-2">
              <Text variant="heading-sm">Webinar</Text>
              <Link
                href="/pelatihan-mandiri/webinar"
                className="text-sm font-semibold text-interactive-default"
              >
                Jadwal lain
              </Link>
            </div>
            <WebinarCard
              id={webinar.id}
              href={`/pelatihan-mandiri/webinar/${webinar.id}`}
              topicName={webinar.topic?.title}
              topicId={webinar.topic?.id as unknown as string}
              title={webinar.title}
              speakers={webinar.speakers}
              startDate={webinar.startDate}
              endDate={webinar.endDate}
            />
          </div>
        ) : null}

        <div>
          <Text variant="heading-sm" className="mb-2">
            Surat Keterangan Selesai Topik
          </Text>
          <CertificateTopic
            progressData={progressionData}
            variant="desktop"
            showHeader={false}
          />
        </div>

        <div>
          <Text variant="heading-sm" className="mb-2">
            Aksi Nyata
          </Text>
          {isAksiNyataEnabled ? (
            <AksiNyataCard
              topikId={topicId}
              name={topicName}
              selectedOptionTitle={
                progressionData?.aksiNyata?.selectedOption?.title
              }
              optionsAmount={topicData?.aksiNyataOptionCount}
              aksiNyataSessionState={progressionData?.aksiNyata?.state}
              isLocked={isAksiNyataLocked}
              aksiNyataId={progressionData?.aksiNyata?.id}
              showHeader={false}
            />
          ) : (
            <div className="rounded-lg border border-gray-20 p-4">
              <Text variant="body-sm" color="subdued">
                Tidak diperlukan untuk menyelesaikan topik ini.
              </Text>
            </div>
          )}
        </div>

        <div>
          <Text variant="heading-sm" className="mb-2">
            Sertifikat
          </Text>
          <CertificateProfileRedirectCard
            certificateStatus={certificateStatus}
            topikId={topicId}
            variant="desktop"
            showHeader={false}
          />
        </div>
      </div>
    </div>
  )

  return (
    <PelatihanDetailShell
      mode="split"
      breadcrumbs={[
        { label: 'Pelatihan Mandiri', href: '/pelatihan-mandiri' },
        { label: topicName, href: null },
      ]}
      sidebar={sidebar}
      actions={
        <Button color="white" size="sm" onClick={onShareClick}>
          <span className="flex items-center gap-2">
            <Icon>share</Icon>
            Bagikan topik
          </span>
        </Button>
      }
    >
      {selectedModuleId ? (
        <ModuleDetails isDesktop displayMode="panel" returnView="topic" />
      ) : (
        <section
          data-testid="empty-module-selection"
          className="flex min-h-[520px] items-center justify-center rounded-lg border border-gray-20 bg-white p-8 text-center"
        >
          <div className="max-w-md">
            <Icon fontSize="lg">book</Icon>
            <Text variant="heading-md" className="mb-2 mt-5">
              Pilih modul untuk mulai belajar
            </Text>
            <Text color="subdued">
              Detail modul, materi, aktivitas, Cerita Reflektif, dan post test
              akan tampil di panel ini.
            </Text>
          </div>
        </section>
      )}
    </PelatihanDetailShell>
  )
}

export default TopicDetailsDesktop
